import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import ImageDropZone from "./ImageDropZone";
import { compressToWebP, withUploadTimeout } from "@/lib/imageCompression";

const AdminDestinations = () => {
  const [destinations, setDestinations] = useState<any[]>([]);
  const [edits, setEdits] = useState<Record<string, any>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [mainImageUrl, setMainImageUrl] = useState<string>("");

  const load = async () => {
    const { data } = await supabase.from("destinations").select("*").order("slot", { ascending: true });
    setDestinations(data || []);
    const e: Record<string, any> = {};
    data?.forEach((d) => { e[d.id] = { name: d.name, description: d.description }; });
    setEdits(e);

    const { data: img } = await supabase.from("site_images").select("url").eq("slot", "destinations-main").maybeSingle();
    if (img?.url) setMainImageUrl(img.url);
  };

  useEffect(() => { load(); }, []);

  const save = async (d: any) => {
    setSaving(d.id);
    const edit = edits[d.id];
    const { error } = await supabase.from("destinations").update({
      name: edit.name,
      description: edit.description,
    }).eq("id", d.id);
    if (error) toast.error("Erreur: " + error.message);
    else toast.success(`${edit.name} mis à jour !`);
    setSaving(null);
  };

  const uploadMainImage = async (file: File) => {
    const webp = await compressToWebP(file, { maxSizeMB: 0.5 });
    const path = `destinations-main.webp`;
    const { error } = await withUploadTimeout(
      supabase.storage.from("site-images").upload(path, webp, { upsert: true, contentType: webp.type })
    );
    if (error) throw new Error("Erreur upload : " + error.message);
    const { data: urlData } = supabase.storage.from("site-images").getPublicUrl(path);
    const url = urlData.publicUrl + "?t=" + Date.now();
    const { error: dbErr } = await supabase.from("site_images").upsert({ slot: "destinations-main", url }, { onConflict: "slot" });
    if (dbErr) throw new Error("Erreur DB : " + dbErr.message);
    setMainImageUrl(url);
    toast.success("Photo principale mise à jour !");
  };

  return (
    <div className="space-y-8">
      {/* Main photo */}
      <Card className="border-border">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Photo principale (grande image à droite)</CardTitle>
        </CardHeader>
        <CardContent>
          <ImageDropZone
            currentUrl={mainImageUrl || undefined}
            onUpload={uploadMainImage}
            accept="image/*"
            label="Glisser ou cliquer pour changer la photo principale"
            height="h-64"
          />
        </CardContent>
      </Card>

      {/* Destination titles/descriptions */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {destinations.map((d) => (
          <Card key={d.id} className="border-border">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-lg">{edits[d.id]?.name || d.name}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <label className="block font-body text-xs text-muted-foreground mb-1">Nom</label>
                <Input value={edits[d.id]?.name || ""} onChange={(e) => setEdits((prev) => ({ ...prev, [d.id]: { ...prev[d.id], name: e.target.value } }))} />
              </div>
              <div>
                <label className="block font-body text-xs text-muted-foreground mb-1">Description</label>
                <Textarea value={edits[d.id]?.description || ""} onChange={(e) => setEdits((prev) => ({ ...prev, [d.id]: { ...prev[d.id], description: e.target.value } }))} rows={2} />
              </div>
              <Button onClick={() => save(d)} disabled={saving === d.id} className="w-full" size="sm">
                {saving === d.id ? "Enregistrement..." : "Enregistrer"}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminDestinations;
